import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getEicrReport, createEicrReport, updateEicrReport,
  fetchEicrReportPdfBlobUrl, uploadEicrPhoto, deleteEicrPhoto, getEicrPresets, getUsers,
  emailEicrReport, previewEicrReportEmail,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Camera, X, Trash2, Download, Mail, Save, Image as ImageIcon, Eye,
} from 'lucide-react';
import AddressLookup from '../../components/AddressLookup';
import { UkAddressFields, formatAddress } from '../../components/UkAddressFields';
import { ContactAutocomplete } from '../../components/ContactAutocomplete';

const CODE_COLOURS = {
  C1: 'bg-rose-100 text-rose-800 border-rose-200',
  C2: 'bg-orange-100 text-orange-800 border-orange-200',
  C3: 'bg-amber-100 text-amber-800 border-amber-200',
  FI: 'bg-rose-100 text-rose-800 border-rose-200',
};

const DEFAULT_REPORT = {
  customerId: '',
  customerName: '',
  customerAddress: '',
  testDate: '',
  location: '',
  testEngineer: '',
  coverPhoto: '',
  coverLogo: '',
  findings: [],
};

const dataUrl = (b64) => (b64 && b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`);

// Client-side image compression — resizes to `maxDim` px on the longest
// side and re-encodes as JPEG at `quality`. Modern iPhone photos are
// typically 4-15 MB; after this pipeline they land at ~300-700 KB while
// staying visually crisp for the PDF grid. HEIC files that can't be
// decoded by the browser fall through unchanged so the backend can
// surface a proper error.
const compressImage = (file, maxDim = 2000, quality = 0.85) => new Promise((resolve) => {
  if (!file || !file.type || !file.type.startsWith('image/')) return resolve(file);
  const reader = new FileReader();
  reader.onerror = () => resolve(file);
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => resolve(file);
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return resolve(file);
      // Skip if already small AND under 2 MB.
      if (w <= maxDim && h <= maxDim && file.size <= 2 * 1024 * 1024) return resolve(file);
      const scale = Math.min(1, maxDim / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(file);
          const name = (file.name || 'photo.jpg').replace(/\.(heic|heif|png|webp|tif|tiff)$/i, '.jpg');
          resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
        },
        'image/jpeg',
        quality,
      );
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const EicrReportEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';

  const [report, setReport] = useState(DEFAULT_REPORT);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [presets, setPresets] = useState({ presets: { C1: [], C2: [], C3: [], FI: [] } });
  const [emailOpen, setEmailOpen] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);   // { url, filename } | null
  const [pdfLoading, setPdfLoading] = useState(false);
  // Small transient "Saved" pill that flashes after each auto-save so
  // the user gets confirmation without a full toast every time.
  const [autoSavedAt, setAutoSavedAt] = useState(0);

  /** Auto-save-on-blur — POST if new (and customer name filled),
   *  PATCH otherwise. Silently ignores blur events on unsaved-with-no-
   *  customer-name to avoid spamming errors while the admin is still
   *  filling the top of the form. */
  const autoSaveOnBlur = React.useCallback(async () => {
    const currentId = report.id || (!isNew ? id : null);
    if (!currentId) {
      if (!report.customerName.trim()) return;   // wait until name typed
      try {
        const created = await createEicrReport(report);
        setReport(created);
        navigate(`${base}/eicr-reports/${created.id}`, { replace: true });
        setAutoSavedAt(Date.now());
      } catch {
        // Silent — user will see the error when they hit Save/Photo.
      }
      return;
    }
    try {
      // Only PATCH the fields the editor actually owns; skip system
      // fields (id/createdAt/status/etc).
      const patch = {
        customerId: report.customerId,
        customerName: report.customerName,
        customerAddress: report.customerAddress,
        testDate: report.testDate,
        location: report.location,
        testEngineer: report.testEngineer,
      };
      const upd = await updateEicrReport(currentId, patch);
      setReport((r) => ({ ...r, ...upd }));
      setAutoSavedAt(Date.now());
    } catch {
      // Silent — again, Save button will surface errors.
    }
  }, [report, id, isNew, base, navigate]);

  useEffect(() => {
    // Load customer list + presets in parallel.
    Promise.all([getUsers().catch(() => []), getEicrPresets().catch(() => null)])
      .then(([users, ps]) => {
        setCustomers(users.filter(u => u.role === 'customer'));
        if (ps) setPresets(ps);
      });
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    getEicrReport(id)
      .then(setReport)
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const save = async () => {
    if (!report.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    // Prefer the id already on the report object (populated after an
    // auto-save on blur/photo pick) so we PATCH instead of POSTing a
    // duplicate draft when the user clicks the header button.
    const persistedId = report.id || (!isNew ? id : null);
    setSaving(true);
    try {
      if (!persistedId) {
        const created = await createEicrReport(report);
        toast.success('Report created');
        setReport(created);
        navigate(`${base}/eicr-reports/${created.id}`, { replace: true });
      } else {
        const upd = await updateEicrReport(persistedId, report);
        // upd is slim (no photo bytes) — merge into current state so we
        // don't nuke locally-loaded photo arrays. `findings` in the slim
        // response also lacks per-finding `photos` keys, so remap and
        // preserve them per-index.
        setReport((prev) => {
          const merged = { ...prev, ...upd };
          if (Array.isArray(upd.findings)) {
            merged.findings = upd.findings.map((f, i) => ({
              ...f,
              photos: (prev.findings?.[i]?.photos) || f.photos || [],
            }));
          }
          if (!('coverPhoto' in upd)) merged.coverPhoto = prev.coverPhoto;
          if (!('coverLogo' in upd))  merged.coverLogo  = prev.coverLogo;
          return merged;
        });
        toast.success('Saved');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (kind, findingIndex, file) => {
    if (!file) return;
    // Compress on-device first so we don't hit the 8 MB backend cap
    // with 10-15 MB iPhone HEIC/JPEGs. Photos in the PDF grid never
    // need more than ~2000 px on the long edge.
    let toUpload = file;
    if (file.size > 2 * 1024 * 1024) {
      const compressToast = toast.loading('Compressing photo…');
      try {
        toUpload = await compressImage(file);
      } catch {
        toUpload = file;
      } finally {
        toast.dismiss(compressToast);
      }
    }
    // Once created, `report.id` is populated from the POST response —
    // use it in preference to the URL param so subsequent photo taps on
    // the same session (after auto-save) still hit the right report.
    let reportId = report.id || (!isNew ? id : null);
    if (!reportId) {
      if (!report.customerName.trim()) {
        toast.error('Enter the customer name before adding a photo');
        return;
      }
      try {
        const created = await createEicrReport(report);
        toast.success('Draft saved — uploading photo…');
        reportId = created.id;
        setReport(created);
        // Use react-router navigate so `useParams().id` updates and the
        // editor flips out of "isNew" mode. `history.replaceState`
        // alone leaves React Router thinking we're still on /new which
        // means the Save button says "Create" and re-POSTs a duplicate
        // draft on every click.
        navigate(`${base}/eicr-reports/${created.id}`, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Save failed');
        return;
      }
    } else if (kind === 'finding') {
      // If the user added or edited a finding page locally (title text
      // change, added a new page) but hasn't saved yet, `findingIndex`
      // might point past the end of what the backend knows about. Push
      // the current findings so the index lines up before uploading.
      try {
        await updateEicrReport(reportId, { findings: report.findings || [] });
      } catch {
        // Non-fatal — if PATCH fails the upload below will surface the
        // real error.
      }
    }
    try {
      if (toUpload.size > 8 * 1024 * 1024) {
        toast.error('Photo is too large even after compression. Please pick a smaller one (or save from iPhone as JPEG).');
        return;
      }
      // Backend now returns a slim delta:
      //   cover     → { coverPhoto: b64 }
      //   coverLogo → { coverLogo: b64 }
      //   finding   → { findingIndex, photos: [b64,...] }
      // Merge into existing state instead of blowing away photos we
      // already have locally.
      const resp = await uploadEicrPhoto(reportId, kind, findingIndex, toUpload);
      setReport((prev) => {
        if (kind === 'cover' && resp?.coverPhoto !== undefined) {
          return { ...prev, coverPhoto: resp.coverPhoto };
        }
        if (kind === 'coverLogo' && resp?.coverLogo !== undefined) {
          return { ...prev, coverLogo: resp.coverLogo };
        }
        if (kind === 'finding' && Array.isArray(resp?.photos)) {
          const fi = typeof resp.findingIndex === 'number' ? resp.findingIndex : findingIndex;
          const findings = (prev.findings || []).map((f, i) => (
            i === fi ? { ...f, photos: resp.photos } : f
          ));
          return { ...prev, findings };
        }
        // Legacy full-doc response (backwards compat).
        return resp && resp.id ? resp : prev;
      });
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const addFinding = () => {
    setReport((r) => ({
      ...r,
      findings: [...(r.findings || []), { location: '', photos: [], observations: [] }],
    }));
  };
  const removeFinding = (idx) => {
    if (!window.confirm('Delete this finding page?')) return;
    setReport((r) => ({
      ...r,
      findings: (r.findings || []).filter((_, i) => i !== idx),
    }));
  };
  const patchFinding = (idx, patch) => {
    setReport((r) => ({
      ...r,
      findings: (r.findings || []).map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  };
  const clearCoverPhoto = async (kind) => {
    const reportId = report.id || (!isNew ? id : null);
    // Optimistic local clear.
    setReport((r) => ({ ...r, [kind === 'cover' ? 'coverPhoto' : 'coverLogo']: '' }));
    if (!reportId) return;
    try {
      await deleteEicrPhoto(reportId, { kind });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete photo on server');
    }
  };

  const removeFindingPhoto = async (fIdx, pIdx) => {    const reportId = report.id || (!isNew ? id : null);
    // Optimistic local update — instant thumbnail removal.
    setReport((r) => ({
      ...r,
      findings: (r.findings || []).map((f, i) => (
        i === fIdx
          ? { ...f, photos: (f.photos || []).filter((_, j) => j !== pIdx) }
          : f
      )),
    }));
    if (!reportId) return;
    try {
      await deleteEicrPhoto(reportId, {
        kind: 'finding', findingIndex: fIdx, photoIndex: pIdx,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete photo on server');
    }
  };
  const addObservation = (fIdx) => {
    patchFinding(fIdx, {
      observations: [...(report.findings[fIdx].observations || []), { code: 'C2', reason: '' }],
    });
  };
  const patchObservation = (fIdx, oIdx, patch) => {
    patchFinding(fIdx, {
      observations: report.findings[fIdx].observations.map((o, i) => (
        i === oIdx ? { ...o, ...patch } : o
      )),
    });
  };
  const removeObservation = (fIdx, oIdx) => {
    patchFinding(fIdx, {
      observations: report.findings[fIdx].observations.filter((_, i) => i !== oIdx),
    });
  };

  // Auth-aware PDF fetch. Returns a { url, filename } object built from
  // an in-memory blob so the browser never has to make an unauthenticated
  // request. Also stamps `updatedAt` on the report by way of an
  // auto-save first — otherwise the PDF might miss the engineer's
  // unsaved keystrokes.
  const fetchPdf = async () => {
    const currentId = report.id || (!isNew ? id : null);
    if (!currentId) {
      toast.error('Save the report first');
      return null;
    }
    setPdfLoading(true);
    try {
      // Best-effort save so the PDF reflects the latest edits.
      try { await updateEicrReport(currentId, report); } catch { /* non-fatal */ }
      return await fetchEicrReportPdfBlobUrl(currentId);
    } catch (e) {
      toast.error(e?.message || 'Failed to build PDF');
      return null;
    } finally {
      setPdfLoading(false);
    }
  };

  const previewPdf = async () => {
    const p = await fetchPdf();
    if (!p) return;
    // iOS Safari doesn't reliably render PDFs inside an iframe — it
    // often shows a blank frame or an "Open in…" prompt. Detect iOS
    // (including iPadOS reporting as MacIntel with touch) and pop the
    // blob URL in a new tab where the native PDF viewer takes over.
    // Delay the revoke so the new tab has time to load.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) {
      window.open(p.url, '_blank');
      setTimeout(() => URL.revokeObjectURL(p.url), 60_000);
      return;
    }
    setPdfPreview(p);
  };

  const downloadPdf = async () => {
    const p = await fetchPdf();
    if (!p) return;
    // Trigger download via a temporary anchor.
    const a = document.createElement('a');
    a.href = p.url;
    a.download = p.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(p.url), 60_000);
  };

  const closePreview = () => {
    if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl">
      <PageHeader
        title={isNew ? 'New EICR Section K report' : `EICR · ${report.id}`}
        description={isNew ? 'Fill in the cover, then add finding pages with photos + observations.' : (
          <>
            <span className="text-slate-500">Created {report.createdAt?.slice(0, 10)} · </span>
            {report.status === 'sent' ? <span className="text-emerald-700 font-medium">Sent to {report.sentTo}</span> : 'Draft'}
          </>
        )}
        actions={
          <div className="flex flex-wrap gap-2 items-center">
            {autoSavedAt > 0 && Date.now() - autoSavedAt < 4000 && (
              <span
                className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full inline-flex items-center gap-1"
                data-testid="eicr-autosaved-pill"
              >
                <Save className="h-3 w-3" /> Saved
              </span>
            )}
            <Link to={`${base}/eicr-reports`}>
              <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            </Link>
            {!isNew && (
              <Button
                variant="outline"
                size="sm"
                onClick={previewPdf}
                disabled={pdfLoading}
                data-testid="eicr-preview-pdf"
              >
                <Eye className="h-4 w-4 mr-1" /> {pdfLoading ? 'Building…' : 'Preview'}
              </Button>
            )}
            {!isNew && (
              <Button
                variant="outline"
                size="sm"
                onClick={downloadPdf}
                disabled={pdfLoading}
                data-testid="eicr-download-pdf"
              >
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </Button>
            )}
            {!isNew && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEmailOpen(true)}
                data-testid="eicr-email-btn"
              >
                <Mail className="h-4 w-4 mr-1" /> Send to customer
              </Button>
            )}
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="eicr-save-btn"
            >
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : (report.id || !isNew) ? 'Save' : 'Create'}
            </Button>
          </div>
        }
      />

      {/* ---- Cover section ---- */}
      <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="eicr-cover-section">
        <div className="text-sm font-semibold text-slate-900">Cover page</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Customer</Label>
            <AddressLookup
              value={report.customerName}
              onChange={(v) => setReport((r) => ({ ...r, customerName: v, customerId: '' }))}
              existingCustomers={customers}
              onCustomerPick={(c) => {
                // Pull the customer's address from their sites (first site with an address wins).
                const site = (c.sites || []).find((s) => s.address);
                setReport((r) => ({
                  ...r,
                  customerId: c.id,
                  customerName: c.company || c.name || '',
                  customerAddress: site?.address || c.address || r.customerAddress,
                }));
              }}
              onAddressPicked={(a) => {
                // Postcode result — put the one-line into the ADDRESS field, keep name.
                setReport((r) => ({ ...r, customerAddress: a.oneLine || a.formatted }));
              }}
              placeholder="Type customer name, postcode or address…"
              testId="eicr-customer-lookup"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Pick an existing customer to auto-fill the address, or use the postcode finder under Address below.
            </div>
          </div>
          <div>
            <Label className="text-xs">Customer name (as shown on the PDF)</Label>
            <Input
              value={report.customerName}
              onChange={(e) => setReport((r) => ({ ...r, customerName: e.target.value }))}
              onBlur={autoSaveOnBlur}
              placeholder="Bell's Healthcare"
              className="mt-1"
              data-testid="eicr-customer-name"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Address</Label>
            <div className="mt-1">
              <UkAddressFields
                value={report.customerAddressParts}
                legacyText={report.customerAddress}
                onChange={(parts) => setReport((r) => ({ ...r, customerAddressParts: parts, customerAddress: formatAddress(parts, ', ') }))}
                testId="eicr-customer-address-lookup"
              />
              <div className="text-[11px] text-slate-500 mt-1" data-testid="eicr-address-pdf-preview">
                On the PDF: {report.customerAddress || '—'}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Test date</Label>
            <Input
              value={report.testDate}
              onChange={(e) => setReport((r) => ({ ...r, testDate: e.target.value }))}
              onBlur={autoSaveOnBlur}
              placeholder="June 2026"
              className="mt-1"
              data-testid="eicr-test-date"
            />
          </div>
          <div>
            <Label className="text-xs">Location / subtitle</Label>
            <Input
              value={report.location}
              onChange={(e) => setReport((r) => ({ ...r, location: e.target.value }))}
              onBlur={autoSaveOnBlur}
              placeholder="Production Site"
              className="mt-1"
              data-testid="eicr-location"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Test engineer</Label>
            <Input
              value={report.testEngineer}
              onChange={(e) => setReport((r) => ({ ...r, testEngineer: e.target.value }))}
              onBlur={autoSaveOnBlur}
              placeholder="Maxmiliano Luca Trombetta"
              className="mt-1"
              data-testid="eicr-engineer"
            />
          </div>
        </div>

        {/* Cover photo + logo uploaders */}
        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <PhotoDrop
            label="Cover photo (site entrance / building)"
            value={report.coverPhoto}
            onFile={(file) => uploadPhoto('cover', -1, file)}
            onClear={() => clearCoverPhoto('cover')}
            testId="eicr-cover-photo"
          />
          <PhotoDrop
            label="Customer / site logo (optional, bottom-right of cover)"
            value={report.coverLogo}
            onFile={(file) => uploadPhoto('coverLogo', -1, file)}
            onClear={() => clearCoverPhoto('coverLogo')}
            testId="eicr-cover-logo"
          />
        </div>
        {isNew && (
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
            Tip: enter the customer name first — the draft saves automatically the moment you pick a photo.
          </div>
        )}
      </section>

      {/* ---- Findings ---- */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">Findings ({report.findings?.length || 0})</div>
          <Button size="sm" variant="outline" onClick={addFinding} data-testid="eicr-add-finding">
            <Plus className="h-4 w-4 mr-1" /> Add finding page
          </Button>
        </div>

        {(report.findings || []).map((f, i) => (
          <FindingCard
            key={i}
            finding={f}
            index={i}
            reportId={id}
            presets={presets}
            onPatch={(patch) => patchFinding(i, patch)}
            onRemove={() => removeFinding(i)}
            onUploadPhoto={(file) => uploadPhoto('finding', i, file)}
            onRemovePhoto={(pIdx) => removeFindingPhoto(i, pIdx)}
            onAddObservation={() => addObservation(i)}
            onPatchObservation={(oIdx, patch) => patchObservation(i, oIdx, patch)}
            onRemoveObservation={(oIdx) => removeObservation(i, oIdx)}
          />
        ))}

        {(!report.findings || report.findings.length === 0) && !isNew && (
          <div className="p-8 text-center border border-dashed border-slate-300 rounded-md text-sm text-slate-500">
            No findings yet. Click <span className="font-medium">Add finding page</span> to record the first one.
          </div>
        )}
      </section>

      {!isNew && (
        <EmailDialog
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          reportId={id}
          customerName={report.customerName}
          onSent={(res) => {
            setReport((r) => ({ ...r, status: 'sent', sentTo: res.to }));
            setEmailOpen(false);
          }}
        />
      )}

      {pdfPreview && (
        <PdfPreviewModal
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          onClose={closePreview}
          onDownload={() => {
            const a = document.createElement('a');
            a.href = pdfPreview.url;
            a.download = pdfPreview.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
        />
      )}
    </div>
  );
};

// Full-screen PDF preview backed by a Blob URL. Uses <iframe> because
// it's the widest-supported way to render a PDF inline on both mobile
// Safari and desktop Chrome. Includes a Download and a Close button.
const PdfPreviewModal = ({ url, filename, onClose, onDownload }) => (
  <div
    className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex flex-col"
    data-testid="eicr-pdf-preview"
    role="dialog"
    aria-label="PDF preview"
  >
    <div className="flex items-center justify-between px-3 py-2 bg-slate-900 text-white gap-2">
      <div className="text-sm truncate">{filename}</div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={onDownload}
          data-testid="eicr-pdf-preview-download"
        >
          <Download className="h-4 w-4 mr-1" /> Download
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="text-white hover:bg-white/10"
          data-testid="eicr-pdf-preview-close"
          aria-label="Close preview"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <iframe
      src={url}
      title="EICR PDF preview"
      className="flex-1 w-full bg-white"
    />
  </div>
);

const PhotoDrop = ({ label, value, onFile, onClear, disabled, testId }) => {
  const inputId = `${testId}-input`;
  return (
    <div>
      <Label className="text-xs mb-1 block">{label}</Label>
      {value ? (
        <div className="relative">
          <img
            src={dataUrl(value)}
            alt=""
            className="w-full h-40 object-cover rounded-md border border-slate-200"
            data-testid={`${testId}-preview`}
          />
          <button
            onClick={onClear}
            disabled={disabled}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center"
            data-testid={`${testId}-clear`}
            aria-label="Remove"
          >
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className={`h-40 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
            disabled ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60' : 'border-slate-300 hover:border-slate-400 bg-slate-50 hover:bg-slate-100'
          }`}
          data-testid={testId}
        >
          <ImageIcon className="h-6 w-6 text-slate-400" />
          <div className="text-xs text-slate-500">Tap to pick photo</div>
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

const FindingCard = ({
  finding, index, reportId, presets, onPatch, onRemove,
  onUploadPhoto, onRemovePhoto, onAddObservation, onPatchObservation, onRemoveObservation,
}) => {
  const inputId = `eicr-finding-${index}-photo`;
  const cameraId = `eicr-finding-${index}-camera`;
  const photos = finding.photos || [];
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid={`eicr-finding-${index}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <Label className="text-xs">Location (shows on the page header)</Label>
          <Input
            value={finding.location || ''}
            onChange={(e) => onPatch({ location: e.target.value })}
            placeholder="Main Corridor"
            className="mt-1"
            data-testid={`eicr-finding-location-${index}`}
          />
        </div>
        <button
          onClick={onRemove}
          className="mt-6 text-slate-400 hover:text-rose-600"
          data-testid={`eicr-remove-finding-${index}`}
          aria-label="Remove finding"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Photos */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {photos.map((p, pIdx) => (
          <div key={pIdx} className="relative">
            <img
              src={dataUrl(p)}
              alt={`finding ${index} photo ${pIdx + 1}`}
              className="w-full h-28 object-cover rounded-md border border-slate-200"
              data-testid={`eicr-finding-photo-${index}-${pIdx}`}
            />
            <button
              onClick={() => onRemovePhoto(pIdx)}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center"
              aria-label="Remove photo"
              data-testid={`eicr-finding-photo-remove-${index}-${pIdx}`}
            >
              <X className="h-3.5 w-3.5 text-slate-700" />
            </button>
          </div>
        ))}
        {photos.length < 4 && (
          <>
            <label
              htmlFor={cameraId}
              className="h-28 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-300 hover:border-slate-400 rounded-md cursor-pointer bg-slate-50 hover:bg-slate-100"
              data-testid={`eicr-finding-camera-${index}`}
            >
              <Camera className="h-5 w-5 text-slate-400" />
              <div className="text-[10px] text-slate-500">Camera</div>
            </label>
            <input
              id={cameraId}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUploadPhoto(f);
                e.target.value = '';
              }}
            />
            <label
              htmlFor={inputId}
              className="h-28 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-300 hover:border-slate-400 rounded-md cursor-pointer bg-slate-50 hover:bg-slate-100"
              data-testid={`eicr-finding-gallery-${index}`}
            >
              <ImageIcon className="h-5 w-5 text-slate-400" />
              <div className="text-[10px] text-slate-500">Gallery</div>
            </label>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUploadPhoto(f);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {/* Observations */}
      <div className="pt-2 border-t border-slate-100 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide">Observations</div>
          <Button size="sm" variant="outline" onClick={onAddObservation} data-testid={`eicr-add-obs-${index}`}>
            <Plus className="h-3 w-3 mr-1" /> Add observation
          </Button>
        </div>
        {(finding.observations || []).map((o, oIdx) => (
          <ObservationRow
            key={oIdx}
            observation={o}
            presets={presets}
            onPatch={(patch) => onPatchObservation(oIdx, patch)}
            onRemove={() => onRemoveObservation(oIdx)}
            testIdPrefix={`eicr-obs-${index}-${oIdx}`}
          />
        ))}
        {(!finding.observations || finding.observations.length === 0) && (
          <div className="text-xs text-slate-400 italic px-2 py-1">
            No observations. Every finding needs at least one for the PDF to make sense.
          </div>
        )}
      </div>
    </div>
  );
};

const ObservationRow = ({ observation, presets, onPatch, onRemove, testIdPrefix }) => {
  const codes = ['C1', 'C2', 'C3', 'FI'];
  const list = presets?.presets?.[observation.code] || [];
  return (
    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
      <div className="flex gap-1">
        {codes.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => onPatch({ code })}
            className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${
              observation.code === code
                ? CODE_COLOURS[code]
                : 'border-slate-200 text-slate-500 hover:bg-slate-50'
            }`}
            data-testid={`${testIdPrefix}-code-${code}`}
          >
            {code}
          </button>
        ))}
      </div>
      <Select
        value={list.includes(observation.reason) ? observation.reason : '__custom__'}
        onValueChange={(v) => {
          if (v === '__custom__') {
            // Keep whatever's already typed.
            return;
          }
          onPatch({ reason: v });
        }}
      >
        <SelectTrigger className="w-full sm:w-56 h-9" data-testid={`${testIdPrefix}-preset`}>
          <SelectValue placeholder="Pick preset…" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          <SelectItem value="__custom__">Custom text →</SelectItem>
          {list.map((r) => (
            <SelectItem key={r} value={r}>{r}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={observation.reason || ''}
        onChange={(e) => onPatch({ reason: e.target.value })}
        placeholder="Reason / description"
        className="flex-1"
        data-testid={`${testIdPrefix}-reason`}
      />
      <button
        onClick={onRemove}
        className="text-slate-400 hover:text-rose-600 shrink-0"
        aria-label="Remove observation"
        data-testid={`${testIdPrefix}-remove`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

const EmailDialog = ({ open, onClose, reportId, customerName, onSent }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [greetingName, setGreetingName] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [ccAdmin, setCcAdmin] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html, sender, to, cc, bcc } | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingHtml, setEditingHtml] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');

  useEffect(() => {
    if (open) {
      setTo('');
      setCc('');
      setBcc('');
      setGreetingName('');
      setShowCcBcc(false);
      setSubject(`EICR Section K — ${customerName || 'Observation Report'}`);
      setMessage('');
      setCcAdmin(true);
      setPreview(null);
      setEditingHtml(false);
      setEditedHtml('');
    }
  }, [open, customerName]);

  // Turn "a@x.com, b@x.com" into ['a@x.com','b@x.com'].
  const splitAddresses = (raw) =>
    (raw || '')
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const buildPayload = () => ({
    to: to.trim(),
    cc: splitAddresses(cc),
    bcc: splitAddresses(bcc),
    subject,
    message,
    cc_admin: ccAdmin,
    greetingName: greetingName.trim() || undefined,
    // When the admin has tweaked the HTML in the Preview screen, ship
    // that verbatim so the tweaks land in the customer's inbox.
    bodyHtml: editingHtml ? editedHtml : undefined,
  });

  const openPreview = async () => {
    if (!to.trim()) return toast.error('Enter a recipient email first');
    setPreviewLoading(true);
    try {
      const res = await previewEicrReportEmail(reportId, buildPayload());
      setPreview(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const send = async () => {
    if (!to.trim()) return toast.error('Enter a recipient email');
    setSending(true);
    try {
      const res = await emailEicrReport(reportId, buildPayload());
      toast.success(`Emailed to ${res.to}`);
      onSent(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="eicr-email-dialog">
        <DialogHeader>
          <DialogTitle>Email report to customer</DialogTitle>
        </DialogHeader>
        {!preview ? (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">To</Label>
                <div className="mt-1">
                  <ContactAutocomplete
                    value={to}
                    onChange={setTo}
                    onPickContact={(h) => {
                      // First token of the picked contact's name becomes
                      // the greeting so "Hi Mike," not "Hi Cammell,".
                      if (h.name) setGreetingName(h.name);
                    }}
                    placeholder="Start typing a name or email…"
                    testId="eicr-email-to"
                  />
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <Label htmlFor="eicr-greeting-name" className="text-[11px] text-slate-500 shrink-0">
                    Greeting name
                  </Label>
                  <Input
                    id="eicr-greeting-name"
                    value={greetingName}
                    onChange={(e) => setGreetingName(e.target.value)}
                    placeholder='e.g. Mike — leave blank to use the customer name'
                    className="h-8 text-xs"
                    data-testid="eicr-email-greeting-name"
                  />
                </div>
              </div>
              {!showCcBcc ? (
                <button
                  type="button"
                  className="text-xs text-blue-700 hover:text-blue-800 underline"
                  onClick={() => setShowCcBcc(true)}
                  data-testid="eicr-email-toggle-ccbcc"
                >
                  + Add Cc / Bcc
                </button>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">Cc (comma-separated)</Label>
                    <div className="mt-1">
                      <ContactAutocomplete
                        value={cc}
                        onChange={setCc}
                        placeholder="pm@customer.com, engineer@lutrom.co.uk"
                        testId="eicr-email-cc"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Bcc (comma-separated)</Label>
                    <div className="mt-1">
                      <ContactAutocomplete
                        value={bcc}
                        onChange={setBcc}
                        placeholder="records@lutrom.co.uk"
                        testId="eicr-email-bcc"
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1"
                  data-testid="eicr-email-subject"
                />
              </div>
              <div>
                <Label className="text-xs">Message (optional — HTML supported)</Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                  placeholder="Please find attached…"
                  data-testid="eicr-email-message"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Wrapped in the standard Lutrom Engineering signature/footer.
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="eicr-email-cc-admin-wrapper">
                <input
                  type="checkbox"
                  checked={ccAdmin}
                  onChange={(e) => setCcAdmin(e.target.checked)}
                  data-testid="eicr-email-cc-admin"
                />
                Also send a copy to the Lutrom admin address
              </label>
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                variant="outline"
                onClick={openPreview}
                disabled={previewLoading || sending}
                data-testid="eicr-email-preview-btn"
              >
                <Eye className="h-4 w-4 mr-1" /> {previewLoading ? 'Building…' : 'Preview email'}
              </Button>
              <Button
                onClick={send}
                disabled={sending}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="eicr-email-send"
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-2" data-testid="eicr-email-preview-panel">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-0.5">
                <div><span className="font-semibold text-slate-700">From:</span> {preview.sender}</div>
                <div><span className="font-semibold text-slate-700">To:</span> {preview.to || <em>none</em>}</div>
                {preview.cc?.length > 0 && (
                  <div><span className="font-semibold text-slate-700">Cc:</span> {preview.cc.join(', ')}</div>
                )}
                {preview.bcc?.length > 0 && (
                  <div><span className="font-semibold text-slate-700">Bcc:</span> {preview.bcc.join(', ')}</div>
                )}
                <div><span className="font-semibold text-slate-700">Subject:</span> {preview.subject}</div>
              </div>
              <iframe
                srcDoc={editingHtml ? editedHtml : preview.html}
                title="Email preview"
                className="w-full h-[360px] rounded-md border border-slate-200 bg-white"
                data-testid="eicr-email-preview-iframe"
              />
              {editingHtml ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] text-slate-500">Editing raw HTML — the preview above updates as you type.</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditingHtml(false); setEditedHtml(''); }}
                      data-testid="eicr-email-cancel-html-edit"
                    >
                      Reset to template
                    </Button>
                  </div>
                  <textarea
                    value={editedHtml}
                    onChange={(e) => setEditedHtml(e.target.value)}
                    rows={10}
                    className="w-full font-mono text-[11px] leading-tight border border-slate-200 rounded-md px-2 py-2 bg-slate-50"
                    data-testid="eicr-email-html-editor"
                    spellCheck={false}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="text-[11px] text-slate-500">
                    The PDF of the report will be attached automatically when you click Send.
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditedHtml(preview.html); setEditingHtml(true); }}
                    data-testid="eicr-email-edit-html-btn"
                  >
                    Edit HTML
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="eicr-email-back-to-edit">
                Back to edit
              </Button>
              <Button
                onClick={send}
                disabled={sending}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="eicr-email-preview-send"
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EicrReportEditor;
